import {
  Eye,
  Save,
  ShieldCheck,
  ShieldOff,
  Trash2,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactElement,
} from 'react';
import type {
  PowerUpAffectedBoardsResponse,
  PowerUpDirectoryEntry,
  PowerUpPolicy,
  PowerUpPolicyPosture,
  PowerUpUsageResponse,
} from '@molt/shared';

import { Button, Dialog, EmptyState, Notice, Skeleton } from '@/components';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { POWER_UP_CATEGORY_KEYS } from './PowerUpDirectory';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

/** The sentinel-split idiom — see `BoardPowerUpList.tsx` for the full ruling. */
const NAME_SENTINEL = String.fromCharCode(0);

export interface EnterprisePowerUpAdminProps {
  readonly directoryEntries: readonly PowerUpDirectoryEntry[];
  readonly policy: PowerUpPolicy | undefined;
  readonly usage: PowerUpUsageResponse | undefined;
  readonly affectedBoards: PowerUpAffectedBoardsResponse | undefined;
  readonly loading?: boolean | undefined;
  readonly directoryLoading?: boolean | undefined;
  readonly usageLoading?: boolean | undefined;
  readonly affectedBoardsLoading?: boolean | undefined;
  readonly error?: string | undefined;
  readonly directoryError?: string | undefined;
  readonly usageError?: string | undefined;
  readonly affectedBoardsError?: string | undefined;
  readonly policyBusy?: boolean | undefined;
  readonly bulkBusyPowerUpId?: string | null | undefined;
  readonly selectedAffectedPowerUpId?: string | null | undefined;
  readonly onSelectAffectedPowerUp: (powerUpId: string | null) => void;
  readonly onSavePolicy: (posture: PowerUpPolicyPosture, powerUpIds: string[]) => void;
  readonly onBulkDisable: (powerUpId: string, keepData: boolean) => void;
  readonly onRetryPolicy: () => void;
  readonly onRetryDirectory: () => void;
  readonly onRetryUsage: () => void;
  readonly onRetryAffectedBoards: () => void;
}

/**
 * Four wire→KEY tables. The record KEYS (`allow_list`, `block_list`) are the
 * WIRE — `onSavePolicy(posture, …)`, the radio `value=` and
 * `powerUpPolicySchema.parse` all carry them. Routing one answers 422.
 */
const POSTURE_LABEL_KEYS: Record<PowerUpPolicyPosture, string> = {
  allow_list: 'powerUps.enterprise.posture.allowList',
  block_list: 'powerUps.enterprise.posture.blockList',
};

const POSTURE_DESCRIPTION_KEYS: Record<PowerUpPolicyPosture, string> = {
  allow_list: 'powerUps.enterprise.postureBody.allowList',
  block_list: 'powerUps.enterprise.postureBody.blockList',
};

/**
 * ⚠ A CHOICE OF KEY, replacing a bare English verb stem concatenated before the
 * name (`${posture === 'allow_list' ? 'Allow' : 'Block'} ${name} Power-Up`).
 * A stem slotted mid-sentence fixes word order, fixes that the word IS a stem,
 * and fixes that exactly one is needed — the `POLICY_FIELD_LABELS` ruling.
 */
const POLICY_ENTRY_KEYS: Record<PowerUpPolicyPosture, string> = {
  allow_list: 'powerUps.enterprise.policy.allowEntry',
  block_list: 'powerUps.enterprise.policy.blockEntry',
};

/**
 * ⚠ Replaces `Save ${POSTURE_LABELS[draftPosture].toLowerCase()} policy`.
 * LOWER-CASING A TRANSLATED LABEL IS A SECOND BUG independent of the slot:
 * German capitalises nouns and Turkish lowercases `I` to a dotless letter. Both
 * designs render byte-identically in English, which is why every existing
 * assertion passes either way and why the ruling is recorded rather than shown.
 */
const SAVE_POLICY_KEYS: Record<PowerUpPolicyPosture, string> = {
  allow_list: 'powerUps.enterprise.savePolicy.allowList',
  block_list: 'powerUps.enterprise.savePolicy.blockList',
};

function powerUpName(
  t: InterfaceTranslator,
  powerUp: Pick<PowerUpDirectoryEntry, 'name'> | { readonly name: string },
): string {
  const trimmed = powerUp.name.trim();
  return trimmed.length === 0 ? t('powerUps.untitledName') : trimmed;
}

function postureIcon(posture: PowerUpPolicyPosture): LucideIcon {
  return posture === 'allow_list' ? ShieldCheck : ShieldOff;
}

function policyCheckboxLabel(
  t: InterfaceTranslator,
  posture: PowerUpPolicyPosture,
  name: string,
): string {
  return t(POLICY_ENTRY_KEYS[posture], { powerUp: name });
}

export function EnterprisePowerUpAdmin({
  directoryEntries,
  policy,
  usage,
  affectedBoards,
  loading = false,
  directoryLoading = false,
  usageLoading = false,
  affectedBoardsLoading = false,
  error,
  directoryError,
  usageError,
  affectedBoardsError,
  policyBusy = false,
  bulkBusyPowerUpId = null,
  selectedAffectedPowerUpId = null,
  onSelectAffectedPowerUp,
  onSavePolicy,
  onBulkDisable,
  onRetryPolicy,
  onRetryDirectory,
  onRetryUsage,
  onRetryAffectedBoards,
}: EnterprisePowerUpAdminProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [draftPosture, setDraftPosture] = useState<PowerUpPolicyPosture>(
    policy?.posture ?? 'block_list',
  );
  const [draftIds, setDraftIds] = useState<Set<string>>(
    () => new Set(policy?.powerUps.map((entry) => entry.id) ?? []),
  );
  const [bulkTargetId, setBulkTargetId] = useState<string | null>(null);
  const [bulkKeepData, setBulkKeepData] = useState(false);
  const cancelBulkRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (policy === undefined) return;
    setDraftPosture(policy.posture);
    setDraftIds(new Set(policy.powerUps.map((entry) => entry.id)));
  }, [policy]);

  const policyDirty = useMemo(() => {
    if (policy === undefined) return false;
    const policyIds = new Set(policy.powerUps.map((entry) => entry.id));
    if (policy.posture !== draftPosture) return true;
    if (policyIds.size !== draftIds.size) return true;
    return [...draftIds].some((id) => !policyIds.has(id));
  }, [draftIds, draftPosture, policy]);

  const selectedAffectedPowerUp =
    usage?.powerUps.find((entry) => entry.powerUp.id === selectedAffectedPowerUpId)
      ?.powerUp ?? null;
  const bulkTarget =
    usage?.powerUps.find((entry) => entry.powerUp.id === bulkTargetId)?.powerUp ?? null;
  const bulkTargetName =
    bulkTarget === null
      ? t('powerUps.enterprise.thisPowerUp')
      : powerUpName(t, bulkTarget);
  const bulkBodyParts = t('powerUps.enterprise.bulkBody', {
    powerUp: NAME_SENTINEL,
  }).split(NAME_SENTINEL);

  function handlePolicyEntryChange(
    event: ChangeEvent<HTMLInputElement>,
    powerUpId: string,
  ): void {
    const checked = event.currentTarget.checked;
    setDraftIds((previous) => {
      const next = new Set(previous);
      if (checked) next.add(powerUpId);
      else next.delete(powerUpId);
      return next;
    });
  }

  function savePolicy(): void {
    onSavePolicy(draftPosture, [...draftIds].sort());
  }

  function openBulkDialog(powerUpId: string): void {
    setBulkTargetId(powerUpId);
    setBulkKeepData(false);
    onSelectAffectedPowerUp(powerUpId);
  }

  function closeBulkDialog(): void {
    setBulkTargetId(null);
    setBulkKeepData(false);
  }

  return (
    <section
      aria-label={t('powerUps.enterprise.heading')}
      className="flex flex-col gap-5 text-ink"
    >
      <div className="flex flex-col gap-1">
        <h2 className="font-display text-xl font-semibold text-ink">
          {t('powerUps.enterprise.heading')}
        </h2>
        <p className="text-sm text-ink-muted">{t('powerUps.enterprise.description')}</p>
      </div>

      {error === undefined ? null : (
        <Notice
          tone="bad"
          title={t('powerUps.enterprise.policyLoadFailedTitle')}
          body={error}
          action={
            <Button type="button" size="sm" variant="secondary" onClick={onRetryPolicy}>
              {t('powerUps.retry')}
            </Button>
          }
        />
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,28rem)]">
        <div className="flex flex-col gap-4">
          {directoryError === undefined ? null : (
            <Notice
              tone="bad"
              title={t('powerUps.enterprise.directoryLoadFailedTitle')}
              body={directoryError}
              action={
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={onRetryDirectory}
                >
                  {t('powerUps.retry')}
                </Button>
              }
            />
          )}

          <section
            aria-labelledby="power-up-policy-heading"
            className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-4 shadow-card"
          >
            <div className="flex flex-col gap-1">
              <h3 id="power-up-policy-heading" className="text-base font-semibold">
                {t('powerUps.enterprise.postureHeading')}
              </h3>
              <p className="text-sm text-ink-muted">
                {t('powerUps.enterprise.postureDescription')}
              </p>
            </div>

            {loading ? (
              <div role="status" className="text-sm text-ink-muted">
                {t('powerUps.enterprise.policyLoading')}
              </div>
            ) : (
              <div
                role="radiogroup"
                aria-label={t('powerUps.enterprise.postureGroupLabel')}
                className="grid gap-2 md:grid-cols-2"
              >
                {(['allow_list', 'block_list'] as const).map((posture) => {
                  const Icon = postureIcon(posture);
                  return (
                    <label
                      key={posture}
                      className={cn(
                        'flex cursor-pointer gap-3 rounded-card border border-border bg-surface p-3 text-sm',
                        draftPosture === posture && 'border-accent',
                      )}
                    >
                      <input
                        type="radio"
                        name="power-up-policy-posture"
                        aria-label={t(POSTURE_LABEL_KEYS[posture])}
                        value={posture}
                        checked={draftPosture === posture}
                        disabled={policyBusy}
                        onChange={() => setDraftPosture(posture)}
                      />
                      <Icon aria-hidden="true" className="mt-0.5 size-4 text-accent" />
                      <span className="flex flex-col gap-1">
                        <span className="font-medium text-ink">
                          {t(POSTURE_LABEL_KEYS[posture])}
                        </span>
                        <span className="text-ink-muted">
                          {t(POSTURE_DESCRIPTION_KEYS[posture])}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {directoryLoading ? (
              <div aria-busy="true" className="flex flex-col gap-2">
                <Skeleton shape="row" count={4} />
              </div>
            ) : directoryEntries.length === 0 ? (
              <p className="rounded-card border border-border bg-surface px-3 py-2 text-sm text-ink-muted">
                {t('powerUps.enterprise.policyEmpty')}
              </p>
            ) : (
              <ul
                aria-label={t('powerUps.enterprise.policyListLabel', {
                  posture: t(POSTURE_LABEL_KEYS[draftPosture]),
                })}
                className="grid gap-2 md:grid-cols-2"
              >
                {directoryEntries.map((entry) => {
                  const name = powerUpName(t, entry);
                  return (
                    <li
                      key={entry.id}
                      className="rounded-card border border-border bg-surface px-3 py-2"
                    >
                      <label className="flex items-start gap-2 text-sm">
                        <input
                          type="checkbox"
                          aria-label={policyCheckboxLabel(t, draftPosture, name)}
                          checked={draftIds.has(entry.id)}
                          disabled={policyBusy}
                          onChange={(event) => handlePolicyEntryChange(event, entry.id)}
                        />
                        <span className="flex min-w-0 flex-col gap-0.5">
                          <span className="font-medium text-ink">
                            {policyCheckboxLabel(t, draftPosture, name)}
                          </span>
                          {/* ⚠ THE ONE VALUE IN THIS SLICE WHOSE RENDERED BYTES
                              MOVE. This drew `entry.category` — the RAW WIRE
                              VALUE — so a policy row read `Molt · file_management`
                              while `PowerUpDirectory` one panel away mapped the
                              same field to `File management`. FIXED, not merely
                              routed. Slot order is REVERSED against
                              `powerUps.entryMeta` because the source reverses
                              it. */}
                          <span className="text-xs text-ink-muted">
                            {t('powerUps.enterprise.policyEntryMeta', {
                              publisher:
                                entry.publisher ?? t('powerUps.unknownPublisher'),
                              category: t(POWER_UP_CATEGORY_KEYS[entry.category]),
                            })}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            <div>
              <Button
                type="button"
                variant="primary"
                busy={policyBusy}
                disabled={!policyDirty || policy === undefined}
                iconStart={<Save aria-hidden="true" className="size-4" />}
                onClick={savePolicy}
              >
                {t(SAVE_POLICY_KEYS[draftPosture])}
              </Button>
            </div>
          </section>

          <section
            aria-labelledby="power-up-usage-heading"
            className="flex flex-col gap-3"
          >
            <div className="flex flex-col gap-1">
              <h3 id="power-up-usage-heading" className="text-base font-semibold">
                {t('powerUps.enterprise.usageHeading')}
              </h3>
              <p className="text-sm text-ink-muted">
                {t('powerUps.enterprise.usageDescription')}
              </p>
            </div>

            {usageError === undefined ? null : (
              <Notice
                tone="bad"
                title={t('powerUps.enterprise.usageLoadFailedTitle')}
                body={usageError}
                action={
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={onRetryUsage}
                  >
                    {t('powerUps.retry')}
                  </Button>
                }
              />
            )}

            {usageLoading ? (
              <div aria-busy="true" className="flex flex-col gap-2">
                <Skeleton shape="block" count={3} />
              </div>
            ) : usage === undefined || usage.powerUps.length === 0 ? (
              <EmptyState
                tone="neutral"
                headingLevel={4}
                title={t('powerUps.enterprise.usageEmptyTitle')}
                body={t('powerUps.enterprise.usageEmptyBody')}
              />
            ) : (
              <ul
                className="flex flex-col gap-3"
                aria-label={t('powerUps.enterprise.usageListLabel')}
              >
                {usage.powerUps.map((entry) => {
                  const name = powerUpName(t, entry.powerUp);
                  const busy = bulkBusyPowerUpId === entry.powerUp.id;
                  return (
                    <li
                      key={entry.powerUp.id}
                      role="article"
                      aria-label={t('powerUps.enterprise.usageRowLabel', {
                        powerUp: name,
                      })}
                      className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-3 shadow-card"
                    >
                      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                        <div className="min-w-0">
                          <h4 className="text-base font-semibold text-ink">{name}</h4>
                          {/* ⚠ PLURAL, and the THIRD ARGUMENT is load-bearing.
                              `interfaceMessage` defaults `count = 0` and selects
                              `forms.other` whenever `count !== 1`, so dropping
                              it renders `1 boards` and no assertion on the
                              `other` form can see it. */}
                          <p className="flex items-center gap-1 text-sm text-ink-muted">
                            <UsersRound aria-hidden="true" className="size-4" />
                            {t(
                              'powerUps.enterprise.boardCount',
                              { count: entry.boardCount },
                              entry.boardCount,
                            )}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          className="sm:max-w-56"
                          iconStart={<Eye aria-hidden="true" className="size-4" />}
                          onClick={() => onSelectAffectedPowerUp(entry.powerUp.id)}
                        >
                          {t('powerUps.enterprise.viewAffected', { powerUp: name })}
                        </Button>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        busy={busy}
                        disabled={entry.boardCount === 0}
                        iconStart={<Trash2 aria-hidden="true" className="size-4" />}
                        onClick={() => openBulkDialog(entry.powerUp.id)}
                      >
                        {t('powerUps.enterprise.bulkDisable', { powerUp: name })}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <section
          aria-labelledby="affected-power-up-boards-heading"
          className="flex flex-col gap-3"
        >
          <h3 id="affected-power-up-boards-heading" className="text-base font-semibold">
            {t('powerUps.enterprise.affectedHeading')}
          </h3>
          {selectedAffectedPowerUp === null ? (
            <p className="text-sm text-ink-muted">
              {t('powerUps.enterprise.affectedPrompt')}
            </p>
          ) : (
            <p className="text-sm text-ink-muted">
              {t('powerUps.enterprise.affectedFor', {
                powerUp: powerUpName(t, selectedAffectedPowerUp),
              })}
            </p>
          )}

          {affectedBoardsError === undefined ? null : (
            <Notice
              tone="bad"
              title={
                selectedAffectedPowerUp === null
                  ? t('powerUps.enterprise.affectedLoadFailedTitle')
                  : t('powerUps.enterprise.affectedLoadFailedForTitle', {
                      powerUp: powerUpName(t, selectedAffectedPowerUp),
                    })
              }
              body={affectedBoardsError}
              action={
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={onRetryAffectedBoards}
                >
                  {t('powerUps.retry')}
                </Button>
              }
            />
          )}

          {affectedBoardsLoading ? (
            <div aria-busy="true" className="flex flex-col gap-2">
              <Skeleton shape="row" count={3} />
            </div>
          ) : selectedAffectedPowerUpId === null ? null : affectedBoards === undefined ||
            affectedBoards.boards.length === 0 ? (
            <EmptyState
              tone="neutral"
              headingLevel={4}
              title={t('powerUps.enterprise.affectedEmptyTitle')}
              body={t('powerUps.enterprise.affectedEmptyBody')}
            />
          ) : (
            <ul
              className="flex flex-col gap-2"
              aria-label={t('powerUps.enterprise.affectedHeading')}
            >
              {affectedBoards.boards.map((board) => (
                <li
                  key={board.id}
                  className="rounded-card border border-border bg-surface px-3 py-2 text-sm"
                >
                  <span className="font-medium text-ink">{board.title}</span>
                  {/* ⚠ A RAW ULID, shown to a human. Fixing it needs a
                      workspace NAME on `PowerUpAffectedBoardsResponse` — a wire
                      change. Reported, never patched. */}
                  <span className="block text-xs text-ink-muted">
                    {t('powerUps.enterprise.affectedWorkspace', {
                      workspace: board.workspaceId,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <Dialog
        open={bulkTargetId !== null}
        onOpenChange={(open) => {
          if (!open) closeBulkDialog();
        }}
        title={t('powerUps.enterprise.bulkTitle', { powerUp: bulkTargetName })}
        description={t('powerUps.enterprise.bulkDescription')}
        size="sm"
        initialFocus={cancelBulkRef}
        footer={
          <>
            <Button
              type="button"
              variant="danger"
              busy={bulkTargetId !== null && bulkBusyPowerUpId === bulkTargetId}
              busyLabel={t('powerUps.enterprise.bulkBusy', {
                powerUp: bulkTargetName,
              })}
              onClick={() => {
                if (bulkTargetId !== null) onBulkDisable(bulkTargetId, bulkKeepData);
                closeBulkDialog();
              }}
            >
              {t('powerUps.enterprise.bulkConfirm', { powerUp: bulkTargetName })}
            </Button>
            <Button
              ref={cancelBulkRef}
              type="button"
              variant="quiet"
              onClick={closeBulkDialog}
            >
              {t('powerUps.cancel')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-ink-muted">
          {bulkBodyParts.length === 2 ? (
            <p>
              {bulkBodyParts[0]}
              <strong className="font-medium text-ink">{bulkTargetName}</strong>
              {bulkBodyParts[1]}
            </p>
          ) : (
            <p>{t('powerUps.enterprise.bulkBody', { powerUp: bulkTargetName })}</p>
          )}
          <label className="flex items-start gap-2 rounded-card border border-border bg-surface-raised px-3 py-2 font-medium text-ink">
            <input
              type="checkbox"
              checked={bulkKeepData}
              onChange={(event) => setBulkKeepData(event.currentTarget.checked)}
            />
            <span>
              {t('powerUps.keepData')}
              <span className="block font-normal text-ink-muted">
                {t('powerUps.enterprise.keepDataHint')}
              </span>
            </span>
          </label>
        </div>
      </Dialog>
    </section>
  );
}
